
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const EcommercePage = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-b from-background to-secondary/20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Protect Your Profit Margins from Expensive Clicks
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Don't let runaway Google Ads costs eat your margins. Upfixe checks every keyword against your product's target CPA before you spend a penny.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild>
                  <Link to="/auth?signup=true">Start Free Account</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/contact">Contact Sales</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
        
        {/* Benefits Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why E-Commerce Businesses Choose Upfixe Ads
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-card p-6 rounded-lg shadow-sm">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">Zero Margin-Killing Keywords</h3>
                <p className="text-muted-foreground">
                  If a CPC is too high to maintain profitability on your products, our AI removes it before the campaign goes live.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg shadow-sm">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M3 3v18h18" />
                    <path d="m19 9-5 5-4-4-3 3" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">True ROAS Protection</h3>
                <p className="text-muted-foreground">
                  Strict daily budget and keyword ceilings ensure you don't blow your monthly ad spend on window shoppers.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg shadow-sm">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <circle cx="12" cy="12" r="10" />
                    <path d="m16 10-4 4-2-2" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">High Intent, Low Waste</h3>
                <p className="text-muted-foreground">
                  Our AI extracts intent-driven keywords directly from your product pages, skipping broad terms that drain budgets.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* How It Works Section */}
        <section className="py-16 bg-secondary/10">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              How Upfixe Works for E-Commerce
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-semibold mb-2">Tell Us About Your Store</h3>
                <p className="text-muted-foreground">
                  Share basic information about your products, goals, and target customers.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-semibold mb-2">AI Creates Campaigns</h3>
                <p className="text-muted-foreground">
                  Our AI builds optimized Shopping, Search, and Display campaigns focused on driving sales.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-semibold mb-2">Grow Your Sales</h3>
                <p className="text-muted-foreground">
                  Start driving more traffic and sales to your online store with effective campaigns.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Features Grid */}
        <section className="py-16 bg-secondary/10">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              E-Commerce Specific Features
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 flex-shrink-0 bg-primary/10 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Performance Tracking</h3>
                  <p className="text-muted-foreground">
                    Track your campaign performance with easy-to-understand metrics focused on sales and ROI.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-12 h-12 flex-shrink-0 bg-primary/10 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-5 0v-15A2.5 2.5 0 0 1 9.5 2z"></path>
                    <path d="M14.5 8A2.5 2.5 0 0 1 17 10.5v9a2.5 2.5 0 0 1-5 0v-9A2.5 2.5 0 0 1 14.5 8z"></path>
                    <path d="M19.5 14a2.5 2.5 0 0 1 2.5 2.5v3a2.5 2.5 0 0 1-5 0v-3a2.5 2.5 0 0 1 2.5-2.5z"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Keyword Suggestions</h3>
                  <p className="text-muted-foreground">
                    Get curated keyword recommendations relevant to your products and target audience.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-12 h-12 flex-shrink-0 bg-primary/10 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M12 20V10" />
                    <path d="M18 20V4" />
                    <path d="M6 20v-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Performance Reporting</h3>
                  <p className="text-muted-foreground">
                    Clear weekly performance reports that highlight what's working and what needs attention.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-12 h-12 flex-shrink-0 bg-primary/10 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <rect width="18" height="18" x="3" y="3" rx="2" />
                    <path d="M3 9h18" />
                    <path d="M3 15h18" />
                    <path d="M9 3v18" />
                    <path d="M15 3v18" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Quick Campaign Launch</h3>
                  <p className="text-muted-foreground">
                    Launch professional campaigns in minutes instead of hours with our streamlined workflow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Links Section */}
        <section className="py-20 px-4 bg-blue-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Maximize Your Google Ads ROI</h2>
              <p className="text-lg text-muted-foreground">
                Learn proven strategies for reducing CPA, optimizing your ad budget, and scaling your e-commerce sales.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">7 proven strategies to cut your cost-per-acquisition in half.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/budget-optimization-guide" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Budget Optimization Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">Complete framework for maximizing ROI on e-commerce ads.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/ai-vs-manual-google-ads" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">AI vs Manual Ads</h3>
                <p className="text-sm text-muted-foreground mb-4">Compare costs and results: agencies vs AI platforms vs DIY.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Grow Your E-Commerce Store?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Start driving more sales with AI-powered Google Ads campaigns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/auth?signup=true">Start Your Free Account</Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-white hover:bg-white hover:text-primary" asChild>
                <Link to="/contact">Schedule a Demo</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EcommercePage;
